/**
 * Super Admin › Cobros. Ronda 1: conectar, registrar y datos fiscales.
 *
 * ESTA PANTALLA TODAVÍA NO COBRA, Y LO DICE
 * =========================================
 *
 * El cobro automático es la ronda siguiente. Aquí se conectan tus cuentas, se
 * capturan los datos fiscales de cada cliente y se anota lo que ya te pagaron.
 * Se dice en la propia pantalla en vez de dejar que el dueño lo deduzca al ver
 * que nadie le cobró.
 *
 * LO QUE SE ENSEÑA PRIMERO ES LO QUE DUELE
 * ========================================
 *
 * Arriba va cuántos clientes NO se pueden facturar. No es un adorno: con
 * CFDI 4.0, si falta el régimen fiscal o el código postal, el timbrado se
 * rechaza — y descubrirlo cuando ya cobraste deja el pago sin factura y al
 * cliente llamando. Verlo antes es lo que evita ese día.
 *
 * LOS SECRETOS NUNCA SE PINTAN
 * ============================
 *
 * El backend devuelve `tiene_valor: true/false`, jamás el valor. El campo se
 * muestra vacío con la marca de «ya guardado»: si el dueño lo deja vacío al
 * guardar, se conserva lo anterior. Sin esa regla, editar el webhook borraría
 * la clave secreta.
 */
import { useState, useEffect, useCallback } from 'react';
import api, { formatApiError } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import FieldHelp from '@/components/help/FieldHelp';
import {
  CreditCard, Building2, Receipt, AlertTriangle, CheckCircle2, Plus, Info, Landmark, Clock,
} from 'lucide-react';
import { usePrompt } from '@/components/admin/PromptDialog';

// Los del SAT que de verdad usan los negocios. Se listan con su número porque
// es lo que el contador del cliente dicta por teléfono.
const REGIMENES = [
  { clave: '601', nombre: '601 — General de Ley Personas Morales' },
  { clave: '603', nombre: '603 — Personas Morales con Fines no Lucrativos' },
  { clave: '605', nombre: '605 — Sueldos y Salarios' },
  { clave: '606', nombre: '606 — Arrendamiento' },
  { clave: '612', nombre: '612 — Actividades Empresariales y Profesionales' },
  { clave: '621', nombre: '621 — Incorporación Fiscal' },
  { clave: '626', nombre: '626 — RESICO' },
];

const USOS_CFDI = [
  { clave: 'G03', nombre: 'G03 — Gastos en general' },
  { clave: 'G01', nombre: 'G01 — Adquisición de mercancías' },
  { clave: 'P01', nombre: 'P01 — Por definir' },
];

export default function Billing() {
  const [formas, setFormas] = useState([]);
  const [nota, setNota] = useState('');
  const [resumen, setResumen] = useState(null);
  const [pagos, setPagos] = useState([]);
  const [negocios, setNegocios] = useState([]);
  const [cargando, setCargando] = useState(true);

  const cargar = useCallback(async () => {
    try {
      const [f, r, p, n] = await Promise.all([
        api.get('/admin/cobros/formas'),
        api.get('/admin/cobros/resumen'),
        api.get('/admin/cobros/pagos', { params: { limite: 100 } }),
        api.get('/admin/tenants', { params: { limit: 100 } }),
      ]);
      setFormas(f.data.formas || []);
      setNota(f.data.nota || '');
      setResumen(r.data);
      setPagos(p.data.pagos || []);
      setNegocios(Array.isArray(n.data) ? n.data : []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  if (cargando) return <div className="p-8 text-sm text-[#64748B]">Cargando cobros…</div>;

  return (
    <div className="p-4 md:p-8 space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Cobros</h1>
        <p className="text-sm text-[#64748B] mt-1 max-w-3xl">
          Lo que tus clientes te pagan a ti. No se mezcla con lo que cada negocio cobra a los
          suyos en su punto de venta: son cuentas distintas y dinero distinto.
        </p>
      </div>

      {/* Aviso honesto de en qué etapa va esto */}
      <div className="rounded-lg border border-sky-200 bg-sky-50 dark:bg-sky-500/10 p-4 text-xs text-[#0F172A] dark:text-inherit flex items-start gap-2">
        <Info className="w-4 h-4 mt-0.5 shrink-0 text-sky-600" />
        <span>
          <strong>El cobro automático ya está construido, pero viene apagado.</strong> Mientras
          el interruptor del servidor esté apagado, «Próximos cobros» te enseña lo que
          <em> pasaría</em>, sin tocar ninguna tarjeta. Míralo antes de encenderlo. Aparte,
          «Cobrar ahora» sí cobra de verdad, uno por uno, y te pide confirmar el monto.
          Si un cobro falla, el cliente entra a <strong>Cobranza</strong>: se reintenta cuatro
          veces y se le avisa — pero <strong>el sistema nunca suspende a nadie solo</strong>,
          te lo propone y tú decides.
        </span>
      </div>

      <Resumen datos={resumen} />
      <FormasDeCobro formas={formas} nota={nota} onCambio={cargar} />
      <ProximosCobros />
      <Cobranza />
      <PruebasEnCurso />
      <TarjetaDelCliente negocios={negocios} />
      <DatosFiscales negocios={negocios} onCambio={cargar} />
      <RegistrarPago negocios={negocios} onRegistrado={cargar} />
      <HistorialPagos pagos={pagos} />
    </div>
  );
}

/** Lo primero que se ve: cuánto entró y a cuántos no puedes facturar. */
function Resumen({ datos }) {
  if (!datos) return null;
  const monedas = Object.entries(datos.cobrado_por_moneda || {});
  const sinDatos = datos.clientes_sin_datos_fiscales || 0;
  return (
    <div className="grid gap-4 md:grid-cols-3">
      <div className="rounded-lg border border-[#E2E8F0] dark:border-white/10 p-4">
        <div className="text-xs text-[#64748B] mb-2">Cobrado (registrado)</div>
        {monedas.length === 0
          ? <div className="text-2xl font-bold text-[#94A3B8]">—</div>
          : monedas.map(([m, v]) => (
              <div key={m} className="text-2xl font-bold tabular-nums">
                {v} <span className="text-sm font-normal text-[#64748B]">{m}</span>
              </div>
            ))}
        {/* Los totales van por moneda y no sumados: sumar pesos con dólares da un
            número que no significa nada y que alguien acabaría usando. */}
        <p className="text-[11px] text-[#64748B] mt-1">Cada moneda por separado, sin convertir.</p>
      </div>

      <div className="rounded-lg border border-[#E2E8F0] dark:border-white/10 p-4">
        <div className="text-xs text-[#64748B] mb-2">Clientes activos</div>
        <div className="text-2xl font-bold tabular-nums">{datos.clientes_activos}</div>
      </div>

      <div className={`rounded-lg border p-4 ${sinDatos > 0
        ? 'border-amber-300 bg-amber-50 dark:bg-amber-500/10'
        : 'border-[#E2E8F0] dark:border-white/10'}`}>
        <div className="text-xs text-[#64748B] mb-2 flex items-center gap-1">
          {sinDatos > 0 && <AlertTriangle className="w-3.5 h-3.5 text-amber-600" />}
          No se pueden facturar
        </div>
        <div className="text-2xl font-bold tabular-nums">{sinDatos}</div>
        <p className="text-[11px] text-[#64748B] mt-1">
          {sinDatos > 0
            ? 'Les falta RFC, régimen o código postal. Sin eso el timbrado se rechaza.'
            : 'Todos tienen sus datos completos.'}
        </p>
      </div>
    </div>
  );
}

function FormasDeCobro({ formas, nota, onCambio }) {
  const [editando, setEditando] = useState(null);
  const [datos, setDatos] = useState({});
  const [guardando, setGuardando] = useState(false);

  const guardar = async (clave) => {
    setGuardando(true);
    try {
      await api.put(`/admin/cobros/formas/${clave}`, { datos });
      toast.success('Cuenta conectada. Las claves quedan cifradas.');
      setEditando(null); setDatos({});
      onCambio?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setGuardando(false);
    }
  };

  return (
    <div className="rounded-lg border border-[#E2E8F0] dark:border-white/10 p-4 space-y-4">
      <div className="flex items-center gap-2 text-sm font-semibold">
        <CreditCard className="w-4 h-4" /> Tus cuentas de cobro
      </div>
      <p className="text-xs text-[#64748B] max-w-3xl">{nota}</p>

      {/* Agrupadas por EMPRESA, no en una lista plana. Son dos cuentas de Stripe
          distintas —una cobra dólares y euros, la otra pesos— y si se ven
          revueltas es cuestión de tiempo que alguien pegue la clave de una en
          el campo de la otra. Ahí el dinero entraría por una empresa y la
          factura saldría por la otra. */}
      {grupos(formas).map((g) => (
        <div key={g.titulo} className="space-y-2">
          <div className="flex items-baseline gap-2 flex-wrap">
            <h3 className="text-sm font-semibold">{g.titulo}</h3>
            {g.subtitulo && <span className="text-xs text-[#64748B]">{g.subtitulo}</span>}
          </div>

      <div className="grid gap-3 md:grid-cols-3">
        {g.formas.map((f) => (
          <div key={f.clave} className="rounded-md border border-[#E2E8F0] dark:border-white/10 p-3">
            <div className="flex items-center justify-between mb-1">
              <span className="text-sm font-medium flex items-center gap-2">
                {f.clave === 'transferencia' ? <Landmark className="w-4 h-4" /> : <CreditCard className="w-4 h-4" />}
                {f.nombre}
              </span>
              {f.conectado
                ? <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                : <AlertTriangle className="w-4 h-4 text-amber-600" />}
            </div>
            <p className="text-xs text-[#64748B] mb-2">{f.ayuda}</p>
            {(f.monedas || []).length > 0 && (
              <p className="text-[11px] text-[#64748B] mb-2">
                Cobra en <strong>{f.monedas.join(' y ')}</strong>.
              </p>
            )}

            {!f.necesita_credenciales ? (
              <p className="text-[11px] text-emerald-600">Siempre disponible: los registras tú a mano.</p>
            ) : editando === f.clave ? (
              <div className="space-y-2">
                {(f.campos || []).map((c) => (
                  <FieldHelp key={c.clave} label={c.etiqueta} htmlFor={`${f.clave}-${c.clave}`} help={c.ayuda}>
                    <Input id={`${f.clave}-${c.clave}`} type="password"
                           placeholder={c.tiene_valor ? '•••••••• (ya guardada)' : ''}
                           onChange={(e) => setDatos((d) => ({ ...d, [c.clave]: e.target.value }))} />
                  </FieldHelp>
                ))}
                <p className="text-[11px] text-[#64748B]">
                  Lo que dejes vacío conserva lo que ya estaba guardado.
                </p>
                <div className="flex gap-2">
                  <Button size="sm" onClick={() => guardar(f.clave)} disabled={guardando}>
                    {guardando ? 'Guardando…' : 'Guardar'}
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => { setEditando(null); setDatos({}); }}>
                    Cancelar
                  </Button>
                </div>
              </div>
            ) : (
              <>
                {!f.conectado && (f.faltan || []).length > 0 && (
                  <p className="text-[11px] text-amber-600 mb-2">Falta: {f.faltan.join(', ')}</p>
                )}
                <Button size="sm" variant="outline" onClick={() => { setEditando(f.clave); setDatos({}); }}>
                  {f.conectado ? 'Cambiar claves' : 'Conectar'}
                </Button>
              </>
            )}
          </div>
        ))}
      </div>
        </div>
      ))}
    </div>
  );
}

/** Reparte las formas de cobro por empresa emisora, respetando el orden en que
 *  las manda el servidor. Lo que no pertenece a ninguna empresa (la
 *  transferencia) va al final, en su propio grupo. */
function grupos(formas) {
  const porEmpresa = new Map();
  const comunes = [];
  (formas || []).forEach((f) => {
    if (!f.empresa) { comunes.push(f); return; }
    if (!porEmpresa.has(f.empresa)) porEmpresa.set(f.empresa, []);
    porEmpresa.get(f.empresa).push(f);
  });
  const fuera = [...porEmpresa.entries()].map(([empresa, lista]) => ({
    titulo: empresa,
    subtitulo: lista[0]?.empresa_documento === 'cfdi'
      ? 'Sus clientes reciben factura CFDI'
      : 'Sus clientes reciben invoice',
    formas: lista,
  }));
  if (comunes.length) {
    fuera.push({ titulo: 'Sirve para las dos empresas', subtitulo: '', formas: comunes });
  }
  return fuera;
}

/** Los datos con los que se le factura a cada cliente, y qué empresa le vende. */
function DatosFiscales({ negocios, onCambio }) {
  const [elegido, setElegido] = useState('');
  const [datos, setDatos] = useState(null);
  const [falta, setFalta] = useState([]);
  const [guardando, setGuardando] = useState(false);

  const abrir = async (id) => {
    setElegido(id);
    try {
      const r = await api.get(`/admin/cobros/tenants/${id}/fiscal`);
      setDatos(r.data.perfil); setFalta(r.data.falta_para_facturar || []);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const guardar = async () => {
    setGuardando(true);
    try {
      const r = await api.put(`/admin/cobros/tenants/${elegido}/fiscal`, datos);
      setDatos(r.data.perfil); setFalta(r.data.falta_para_facturar || []);
      toast.success('Datos fiscales guardados');
      onCambio?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setGuardando(false); }
  };

  const negocio = negocios.find((n) => n.id === elegido);
  // La empresa que vende la decide el PAÍS del cliente, no la moneda. Se enseña
  // aquí para que el dueño lo vea antes de facturar, no después.
  const esMexico = ['mexico', 'méxico', 'mx'].includes((negocio?.country || '').toLowerCase());

  return (
    <div className="rounded-lg border border-[#E2E8F0] dark:border-white/10 p-4 space-y-4">
      <div className="flex items-center gap-2 text-sm font-semibold">
        <Building2 className="w-4 h-4" /> Datos para facturar a un cliente
      </div>

      <Select value={elegido} onValueChange={abrir}>
        <SelectTrigger className="w-[340px]"><SelectValue placeholder="Elige el negocio" /></SelectTrigger>
        <SelectContent>
          {negocios.map((n) => <SelectItem key={n.id} value={n.id}>{n.name}</SelectItem>)}
        </SelectContent>
      </Select>

      {datos && (
        <>
          <div className={`rounded-md p-3 text-xs ${esMexico
            ? 'bg-[#F1F5F9] dark:bg-white/5' : 'bg-emerald-50 dark:bg-emerald-500/10'}`}>
            {esMexico ? (
              <>Este cliente está en México, así que le vende <strong>Dragon Technologies SAPI de CV</strong> y
              recibe <strong>factura CFDI timbrada</strong>. Requiere los datos completos de abajo.</>
            ) : (
              <>Este cliente está fuera de México ({negocio?.country || 'país sin capturar'}), así que le vende{' '}
              <strong>Dragon Technologies LLC</strong> y recibe <strong>invoice</strong>. No necesita RFC ni
              régimen: con la razón social y el correo basta.</>
            )}
          </div>

          {falta.length > 0 && esMexico && (
            <p className="text-xs text-amber-600 flex items-center gap-1">
              <AlertTriangle className="w-3.5 h-3.5" /> Falta para poder timbrarle: {falta.join(', ')}
            </p>
          )}

          <div className="grid gap-3 md:grid-cols-2">
            <FieldHelp label="Razón social" htmlFor="razon" help="El nombre legal exacto, como aparece en su constancia fiscal.">
              <Input id="razon" value={datos.razon_social || ''}
                     onChange={(e) => setDatos({ ...datos, razon_social: e.target.value })} />
            </FieldHelp>
            <FieldHelp label="Correo para facturas" htmlFor="mail" help="A dónde le llega su factura o invoice.">
              <Input id="mail" value={datos.email_facturacion || ''}
                     onChange={(e) => setDatos({ ...datos, email_facturacion: e.target.value })} />
            </FieldHelp>
            {esMexico && (
              <>
                <FieldHelp label="RFC" htmlFor="rfc" help="Se guarda en mayúsculas: el SAT lo rechaza en minúsculas.">
                  <Input id="rfc" value={datos.rfc || ''}
                         onChange={(e) => setDatos({ ...datos, rfc: e.target.value.toUpperCase() })} />
                </FieldHelp>
                <FieldHelp label="Código postal fiscal" htmlFor="cp" help="El de su domicilio fiscal, no el de la sucursal. Obligatorio desde CFDI 4.0.">
                  <Input id="cp" value={datos.codigo_postal || ''}
                         onChange={(e) => setDatos({ ...datos, codigo_postal: e.target.value })} />
                </FieldHelp>
                <FieldHelp label="Régimen fiscal" htmlFor="reg" help="Viene en su constancia de situación fiscal. Si no coincide, el timbrado se rechaza.">
                  <Select value={datos.regimen_fiscal || ''}
                          onValueChange={(v) => setDatos({ ...datos, regimen_fiscal: v })}>
                    <SelectTrigger id="reg"><SelectValue placeholder="Elige el régimen" /></SelectTrigger>
                    <SelectContent>
                      {REGIMENES.map((r) => <SelectItem key={r.clave} value={r.clave}>{r.nombre}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </FieldHelp>
                <FieldHelp label="Uso del CFDI" htmlFor="uso" help="Para qué usará la factura. Casi siempre «Gastos en general».">
                  <Select value={datos.uso_cfdi || 'G03'}
                          onValueChange={(v) => setDatos({ ...datos, uso_cfdi: v })}>
                    <SelectTrigger id="uso"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {USOS_CFDI.map((u) => <SelectItem key={u.clave} value={u.clave}>{u.nombre}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </FieldHelp>
              </>
            )}
          </div>
          <Button onClick={guardar} disabled={guardando}>
            {guardando ? 'Guardando…' : 'Guardar datos fiscales'}
          </Button>
        </>
      )}
    </div>
  );
}

/** Anotar una transferencia. Es el dinero que hoy no queda registrado. */
function RegistrarPago({ negocios, onRegistrado }) {
  const [abierto, setAbierto] = useState(false);
  const [f, setF] = useState({ tenant_id: '', monto: '', moneda: 'MXN', concepto: '', referencia_externa: '', notas: '' });
  const [guardando, setGuardando] = useState(false);

  const registrar = async () => {
    if (!f.tenant_id || !f.monto) { toast.error('Elige el negocio y escribe el monto'); return; }
    setGuardando(true);
    try {
      const r = await api.post('/admin/cobros/pagos', f);
      toast.success(`Registrado con folio ${r.data.pago.folio || '—'}`);
      setF({ tenant_id: '', monto: '', moneda: 'MXN', concepto: '', referencia_externa: '', notas: '' });
      setAbierto(false);
      onRegistrado?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setGuardando(false); }
  };

  return (
    <div className="rounded-lg border border-[#E2E8F0] dark:border-white/10 p-4 space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-sm font-semibold flex items-center gap-2">
          <Receipt className="w-4 h-4" /> Registrar un pago recibido
        </span>
        <Button size="sm" variant={abierto ? 'ghost' : 'outline'} onClick={() => setAbierto(!abierto)}>
          <Plus className="w-4 h-4 mr-1" /> {abierto ? 'Cerrar' : 'Anotar transferencia'}
        </Button>
      </div>
      <p className="text-xs text-[#64748B]">
        Para lo que te pagan por transferencia o depósito. El folio y la empresa que emite se
        asignan solos según el país del cliente.
      </p>

      {abierto && (
        <div className="grid gap-3 md:grid-cols-3 pt-2 border-t border-[#F1F5F9] dark:border-white/10">
          <FieldHelp label="Negocio" htmlFor="pg-t" help="Quién te pagó.">
            <Select value={f.tenant_id} onValueChange={(v) => setF({ ...f, tenant_id: v })}>
              <SelectTrigger id="pg-t"><SelectValue placeholder="Elige" /></SelectTrigger>
              <SelectContent>
                {negocios.map((n) => <SelectItem key={n.id} value={n.id}>{n.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </FieldHelp>
          <FieldHelp label="Monto" htmlFor="pg-m" help="Solo el número, ej. 2490.00">
            <Input id="pg-m" value={f.monto} placeholder="2490.00"
                   onChange={(e) => setF({ ...f, monto: e.target.value })} />
          </FieldHelp>
          <FieldHelp label="Moneda" htmlFor="pg-mo" help="La moneda en que te llegó el dinero.">
            <Select value={f.moneda} onValueChange={(v) => setF({ ...f, moneda: v })}>
              <SelectTrigger id="pg-mo"><SelectValue /></SelectTrigger>
              <SelectContent>
                {['MXN', 'USD', 'EUR', 'COP'].map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
              </SelectContent>
            </Select>
          </FieldHelp>
          <FieldHelp label="Concepto" htmlFor="pg-c" help="Ej. «Mensualidad agosto». Si lo dejas vacío se pone uno solo.">
            <Input id="pg-c" value={f.concepto} onChange={(e) => setF({ ...f, concepto: e.target.value })} />
          </FieldHelp>
          <FieldHelp label="Referencia" htmlFor="pg-r" help="El folio de la transferencia, para conciliar con tu banco.">
            <Input id="pg-r" value={f.referencia_externa}
                   onChange={(e) => setF({ ...f, referencia_externa: e.target.value })} />
          </FieldHelp>
          <FieldHelp label="Notas" htmlFor="pg-n" help="Lo que quieras recordar de este pago.">
            <Textarea id="pg-n" rows={1} value={f.notas} onChange={(e) => setF({ ...f, notas: e.target.value })} />
          </FieldHelp>
          <div className="md:col-span-3">
            <Button onClick={registrar} disabled={guardando}>
              {guardando ? 'Registrando…' : 'Registrar pago'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function HistorialPagos({ pagos }) {
  return (
    <div className="rounded-lg border border-[#E2E8F0] dark:border-white/10 p-4">
      <div className="text-sm font-semibold mb-3">Pagos registrados</div>
      {pagos.length === 0 ? (
        <p className="text-xs text-[#64748B]">
          Todavía no hay pagos registrados. Anota aquí las transferencias que ya recibiste para
          empezar a tener el historial.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="text-[#64748B]">
              <tr className="text-left">
                <th className="py-2 pr-4">Folio</th>
                <th className="py-2 pr-4">Negocio</th>
                <th className="py-2 pr-4">Concepto</th>
                <th className="py-2 pr-4 text-right">Monto</th>
                <th className="py-2 pr-4">Forma</th>
                <th className="py-2 pr-4">Estado</th>
                <th className="py-2">Factura</th>
              </tr>
            </thead>
            <tbody>
              {pagos.map((p) => (
                <tr key={p.id} className="border-t border-[#E2E8F0] dark:border-white/10">
                  <td className="py-2 pr-4 font-mono">{p.folio || '—'}</td>
                  <td className="py-2 pr-4">{p.negocio}</td>
                  <td className="py-2 pr-4">{p.concepto}</td>
                  <td className="py-2 pr-4 text-right tabular-nums">{p.monto} {p.moneda}</td>
                  <td className="py-2 pr-4">{p.forma_de_pago}</td>
                  <td className="py-2 pr-4">{p.estado}</td>
                  <td className="py-2 text-[#64748B]">
                    {p.cfdi_uuid ? 'Timbrada' : (p.cfdi_estado || 'Pendiente de emitir')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}


/** Que haria HOY el cobro automatico, sin cobrar nada.
 *
 *  Es la pantalla que hay que mirar antes de encender el interruptor por
 *  primera vez: ensena a quien le tocaria, cuanto, y a quien no se le podria
 *  cobrar y por que. Enciende con la variable COBRO_AUTOMATICO_ACTIVO en el
 *  servidor, no desde aqui — el interruptor de algo que mueve dinero no debe
 *  estar a un clic de distancia en una pantalla web.
 */
function ProximosCobros() {
  const [datos, setDatos] = useState(null);
  const [error, setError] = useState(null);
  const [cargando, setCargando] = useState(true);

  useEffect(() => {
    setCargando(true);
    api.get('/admin/cobros/proximos-cobros')
      .then(({ data }) => { setDatos(data); setError(null); })
      .catch((e) => { setDatos(null); setError(formatApiError(e.response?.data?.detail) || 'No se pudo cargar'); })
      .finally(() => setCargando(false));
  }, []);

  if (cargando) {
    return (
      <div className="rounded-lg border border-[#E2E8F0] dark:border-white/10 p-4 text-sm text-[#64748B]">
        Cargando próximos cobros…
      </div>
    );
  }
  if (error || !datos) {
    return (
      <div className="rounded-lg border border-rose-200 bg-rose-50/40 p-4 text-sm text-rose-800">
        No se pudieron cargar los próximos cobros{error ? `: ${error}` : '.'}
      </div>
    );
  }
  const encendido = datos.cobro_automatico_encendido;

  return (
    <div className="rounded-lg border border-[#E2E8F0] dark:border-white/10 p-4 space-y-3">
      <div className="flex items-center gap-2 text-sm font-semibold">
        <Clock className="w-4 h-4" /> Proximos cobros
        <span className={`text-[11px] px-2 py-0.5 rounded-full ${encendido
          ? 'bg-emerald-100 text-emerald-700'
          : 'bg-amber-100 text-amber-700'}`}>
          {encendido ? 'Cobro automatico ENCENDIDO' : 'Cobro automatico APAGADO'}
        </span>
      </div>
      <p className="text-xs text-[#64748B]">{datos.aviso}</p>

      {(datos.pendientes || []).length === 0 ? (
        <p className="text-xs text-[#64748B]">Hoy no le toca cobrar a nadie.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-[#64748B] border-b border-[#E2E8F0] dark:border-white/10">
                <th className="py-2">Negocio</th>
                <th className="py-2 text-right">Monto</th>
                <th className="py-2">Que pasaria</th>
              </tr>
            </thead>
            <tbody>
              {datos.pendientes.map((f) => (
                <tr key={f.tenant_id} className="border-b border-[#E2E8F0] dark:border-white/10 last:border-0">
                  <td className="py-2">{f.negocio}</td>
                  <td className="py-2 text-right tabular-nums">{f.monto} {f.moneda}</td>
                  <td className={`py-2 text-xs ${f.se_puede ? 'text-[#64748B]' : 'text-amber-600'}`}>
                    {f.motivo}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/** La tarjeta guardada de un cliente: verla, mandarle el enlace, cobrarle ya. */
function TarjetaDelCliente({ negocios }) {
  const [elegido, setElegido] = useState('');
  const [datos, setDatos] = useState(null);
  const [ocupado, setOcupado] = useState(false);
  const [enlace, setEnlace] = useState('');

  const cargar = async (id) => {
    if (!id) { setDatos(null); return; }
    try {
      const { data } = await api.get(`/admin/cobros/tenants/${id}/tarjeta`);
      setDatos(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setDatos(null);
    }
  };

  const pedirEnlace = async () => {
    setOcupado(true);
    try {
      const { data } = await api.post(`/admin/cobros/tenants/${elegido}/enlace-de-tarjeta`);
      setEnlace(data.enlace);
      toast.success(data.aviso);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setOcupado(false);
    }
  };

  const cobrar = async () => {
    const p = datos?.proximo_cobro;
    if (!p) return;
    // Se confirma con el monto EXACTO que esta pantalla enseno. Si cambio
    // mientras estaba abierta, el servidor lo rechaza en vez de cobrar otro.
    if (!window.confirm(`Vas a cobrar ${p.monto} ${p.moneda} a ${datos.negocio}. Esto mueve dinero de verdad. Continuar?`)) return;
    setOcupado(true);
    try {
      const { data } = await api.post(`/admin/cobros/tenants/${elegido}/cobrar-ahora`, {
        monto_confirmado: p.monto, moneda_confirmada: p.moneda,
      });
      toast.success(data.mensaje);
      await cargar(elegido);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setOcupado(false);
    }
  };

  return (
    <div className="rounded-lg border border-[#E2E8F0] dark:border-white/10 p-4 space-y-3">
      <div className="flex items-center gap-2 text-sm font-semibold">
        <CreditCard className="w-4 h-4" /> Tarjeta de un cliente
      </div>

      <FieldHelp label="Elige el negocio" htmlFor="tarjeta-negocio"
                 help="Aqui ves si ya dejo su tarjeta, le mandas el enlace para que la ponga, o le cobras a mano.">
        <select id="tarjeta-negocio" value={elegido}
                onChange={(e) => { setElegido(e.target.value); setEnlace(''); cargar(e.target.value); }}
                className="w-full px-3 py-2 text-sm border border-[#E2E8F0] dark:border-white/10 rounded-lg bg-transparent">
          <option value="">Selecciona...</option>
          {(negocios || []).map((n) => (
            <option key={n.id} value={n.id}>{n.name}</option>
          ))}
        </select>
      </FieldHelp>

      {datos && (
        <div className="space-y-3">
          <p className="text-xs text-[#64748B]">
            Le cobra <strong>{datos.empresa_que_cobra}</strong>.
          </p>

          <div className="grid gap-3 md:grid-cols-2">
            <div className="rounded-md bg-[#F8F9FA] dark:bg-white/5 p-3">
              <div className="text-xs text-[#64748B] mb-1">Su tarjeta</div>
              {datos.tarjeta ? (
                <div className="text-sm font-semibold">
                  {(datos.tarjeta.marca || '').toUpperCase()} ****{datos.tarjeta.ultimos4}
                  <span className="ml-2 text-xs font-normal text-[#64748B]">
                    vence {String(datos.tarjeta.vence_mes).padStart(2, '0')}/{datos.tarjeta.vence_anio}
                  </span>
                </div>
              ) : (
                <div className="text-sm text-amber-600">Todavia no ha guardado ninguna</div>
              )}
            </div>

            <div className="rounded-md bg-[#F8F9FA] dark:bg-white/5 p-3">
              <div className="text-xs text-[#64748B] mb-1">Su proximo cobro</div>
              {datos.proximo_cobro?.hay_paquete ? (
                <>
                  <div className="text-sm font-semibold tabular-nums">
                    {datos.proximo_cobro.monto} {datos.proximo_cobro.moneda}
                  </div>
                  <div className="text-xs text-[#64748B]">
                    {datos.proximo_cobro.fecha || 'sin fecha capturada'} · {datos.proximo_cobro.periodicidad}
                  </div>
                </>
              ) : (
                <div className="text-sm text-amber-600">No tiene paquete asignado</div>
              )}
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="outline" onClick={pedirEnlace} disabled={ocupado}>
              {datos.tarjeta ? 'Mandar enlace para cambiarla' : 'Mandar enlace para que la ponga'}
            </Button>
            {datos.tarjeta && datos.proximo_cobro?.hay_paquete && (
              <Button size="sm" onClick={cobrar} disabled={ocupado} data-testid="cobrar-ahora">
                Cobrar ahora
              </Button>
            )}
          </div>

          {enlace && (
            <div className="rounded-md border border-[#E2E8F0] dark:border-white/10 p-3">
              <p className="text-xs text-[#64748B] mb-2">
                Copia esto y mandaselo. No cobra nada: solo guarda su tarjeta.
              </p>
              <div className="flex gap-2">
                <input readOnly value={enlace}
                       className="flex-1 px-2 py-1.5 text-xs border border-[#E2E8F0] dark:border-white/10 rounded bg-transparent" />
                <Button size="sm" variant="outline"
                        onClick={() => { navigator.clipboard?.writeText(enlace); toast.success('Copiado'); }}>
                  Copiar
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}


/** Quien no pago, en que punto va, y el boton que apaga su servicio.
 *
 *  Los que dicen «propuesto» ESPERAN TU DECISION. El sistema hizo todo el
 *  trabajo —reintento cuatro veces y le aviso— pero no corta solo. Un cliente
 *  que pago por transferencia y no alcanzo a registrarse aparecería aqui, y un
 *  automatismo le habria apagado el servicio estando al corriente.
 */
function Cobranza() {
  const { ask, ui: promptUi } = usePrompt();
  const [datos, setDatos] = useState(null);
  const [error, setError] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [ocupado, setOcupado] = useState('');

  const cargar = () => {
    setCargando(true);
    return api.get('/admin/cobros/cobranza')
      .then(({ data }) => { setDatos(data); setError(null); })
      .catch((e) => {
        setDatos(null);
        setError(formatApiError(e.response?.data?.detail) || 'No se pudo cargar');
      })
      .finally(() => setCargando(false));
  };

  useEffect(() => { cargar(); }, []);

  const suspender = async (fila) => {
    const escrito = await ask({
      title: `Apagar servicio de «${fila.negocio}»`,
      description:
        'Se apaga la IA y los envíos. Su equipo sigue entrando y los mensajes de '
        + 'clientes siguen llegando.\n\nPara confirmar, escribe el nombre del negocio:',
      placeholder: fila.negocio,
    });
    if (!escrito) return;
    setOcupado(fila.tenant_id);
    try {
      const { data } = await api.post(`/admin/cobros/cobranza/${fila.tenant_id}/suspender`,
                                      { nombre_confirmado: escrito });
      toast.success(data.mensaje);
      await cargar();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setOcupado('');
    }
  };

  const reactivar = async (fila) => {
    setOcupado(fila.tenant_id);
    try {
      const { data } = await api.post(`/admin/cobros/cobranza/${fila.tenant_id}/reactivar`,
                                      { motivo: 'Restablecido desde Cobros' });
      toast.success(data.mensaje);
      await cargar();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setOcupado('');
    }
  };

  if (cargando && !datos) {
    return (
      <div className="rounded-lg border border-[#E2E8F0] dark:border-white/10 p-4 text-sm text-[#64748B]"
           data-testid="cobranza">
        Cargando cobranza…
      </div>
    );
  }
  if (error || !datos) {
    return (
      <div className="rounded-lg border border-rose-200 bg-rose-50/40 p-4 text-sm text-rose-800"
           data-testid="cobranza">
        No se pudo cargar cobranza{error ? `: ${error}` : '.'}
      </div>
    );
  }
  const filas = datos.en_cobranza || [];

  const ETIQUETA = {
    reintentando: { texto: 'Reintentando', clase: 'bg-amber-100 text-amber-700' },
    propuesto: { texto: 'Espera tu decision', clase: 'bg-red-100 text-red-700' },
    suspendido: { texto: 'Suspendido', clase: 'bg-slate-200 text-slate-700' },
  };

  return (
    <div className="rounded-lg border border-[#E2E8F0] dark:border-white/10 p-4 space-y-3"
         data-testid="cobranza">
      {promptUi}
      <div className="flex items-center gap-2 text-sm font-semibold">
        <AlertTriangle className="w-4 h-4" /> Cobranza
      </div>
      <p className="text-xs text-[#64748B] max-w-3xl">{datos.aviso}</p>

      {filas.length === 0 ? (
        <p className="text-xs text-emerald-600">Nadie te debe. Todos al corriente.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-[#64748B] border-b border-[#E2E8F0] dark:border-white/10">
                <th className="py-2">Negocio</th>
                <th className="py-2">Situacion</th>
                <th className="py-2 text-right">Debe</th>
                <th className="py-2">Intentos</th>
                <th className="py-2"></th>
              </tr>
            </thead>
            <tbody>
              {filas.map((f) => {
                const e = ETIQUETA[f.estado] || { texto: f.estado, clase: '' };
                return (
                  <tr key={f.tenant_id} className="border-b border-[#E2E8F0] dark:border-white/10 last:border-0">
                    <td className="py-2">{f.negocio}</td>
                    <td className="py-2">
                      <span className={`text-[11px] px-2 py-0.5 rounded-full ${e.clase}`}>{e.texto}</span>
                    </td>
                    <td className="py-2 text-right tabular-nums">{f.monto} {f.moneda}</td>
                    <td className="py-2 text-xs text-[#64748B]">
                      {f.intentos_hechos} de {f.intentos_totales}
                      {f.proximo_reintento && <> · siguiente el {f.proximo_reintento}</>}
                    </td>
                    <td className="py-2 text-right">
                      {f.estado === 'propuesto' && (
                        <Button size="sm" variant="outline" disabled={ocupado === f.tenant_id}
                                onClick={() => suspender(f)} data-testid="suspender">
                          Apagar su servicio
                        </Button>
                      )}
                      {f.estado === 'suspendido' && (
                        <Button size="sm" disabled={ocupado === f.tenant_id}
                                onClick={() => reactivar(f)}>
                          Reactivar
                        </Button>
                      )}
                      {f.estado === 'reintentando' && (
                        <Button size="sm" variant="ghost" disabled={ocupado === f.tenant_id}
                                onClick={() => reactivar(f)}
                                title="Usalo si ya te pago por transferencia">
                          Ya me pago
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <details className="text-xs text-[#64748B]">
        <summary className="cursor-pointer">Que pierde exactamente un negocio suspendido</summary>
        <p className="mt-2">
          <strong>Se le apaga:</strong> la respuesta automatica de la IA, las campanas y envios
          masivos, las llamadas de voz y las publicaciones en redes.
        </p>
        <p className="mt-1">
          <strong>Sigue funcionando:</strong> su equipo entra normal, los mensajes de sus clientes
          siguen llegando y guardandose, y no pierde ningun prospecto. Puede atender a mano.
        </p>
      </details>
    </div>
  );
}


/** Quien esta en prueba gratis, cuanto le queda y si ya dejo tarjeta.
 *
 *  La columna que importa es la ultima. Una prueba que termina sin tarjeta es
 *  un cliente que se va sin pagar — y para entonces ya le diste soporte, le
 *  configuraste el WhatsApp y le entrenaste la IA. Verlo tres dias antes es lo
 *  que permite hacer la llamada a tiempo.
 */
function PruebasEnCurso() {
  const [datos, setDatos] = useState(null);
  const [error, setError] = useState(null);
  const [cargando, setCargando] = useState(true);

  useEffect(() => {
    setCargando(true);
    api.get('/admin/cobros/pruebas')
      .then(({ data }) => { setDatos(data); setError(null); })
      .catch((e) => { setDatos(null); setError(formatApiError(e.response?.data?.detail) || 'No se pudo cargar'); })
      .finally(() => setCargando(false));
  }, []);

  if (cargando) {
    return (
      <div className="rounded-lg border border-[#E2E8F0] dark:border-white/10 p-4 text-sm text-[#64748B]"
           data-testid="pruebas-en-curso">
        Cargando pruebas gratis…
      </div>
    );
  }
  if (error || !datos) {
    return (
      <div className="rounded-lg border border-rose-200 bg-rose-50/40 p-4 text-sm text-rose-800"
           data-testid="pruebas-en-curso">
        No se pudieron cargar las pruebas{error ? `: ${error}` : '.'}
      </div>
    );
  }
  const filas = datos.pruebas || [];

  return (
    <div className="rounded-lg border border-[#E2E8F0] dark:border-white/10 p-4 space-y-3"
         data-testid="pruebas-en-curso">
      <div className="flex items-center gap-2 text-sm font-semibold">
        <Clock className="w-4 h-4" /> Pruebas gratis en curso
      </div>
      <p className="text-xs text-[#64748B] max-w-3xl">{datos.aviso}</p>

      {filas.length === 0 ? (
        <p className="text-xs text-emerald-600">No hay pruebas gratis en curso.</p>
      ) : (
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-[#64748B] border-b border-[#E2E8F0] dark:border-white/10">
              <th className="py-2">Negocio</th>
              <th className="py-2">Le queda</th>
              <th className="py-2 text-right">Primer cobro</th>
              <th className="py-2">Tarjeta</th>
            </tr>
          </thead>
          <tbody>
            {filas.map((f) => (
              <tr key={f.tenant_id} className="border-b border-[#E2E8F0] dark:border-white/10 last:border-0">
                <td className="py-2">{f.negocio}</td>
                <td className="py-2 text-xs">
                  {f.dias_restantes === 0 ? 'Termina hoy'
                    : f.dias_restantes === 1 ? 'Termina manana'
                    : `${f.dias_restantes} dias`}
                  {f.termina && <span className="text-[#64748B]"> · {f.termina}</span>}
                </td>
                <td className="py-2 text-right tabular-nums">{f.primer_cobro} {f.moneda}</td>
                <td className="py-2">
                  {f.tiene_tarjeta
                    ? <span className="text-[11px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">Ya la dejo</span>
                    : <span className="text-[11px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">Sin tarjeta</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      )}
    </div>
  );
}
